import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Seconds elapsed in the current take, with a hard stop at `limitSeconds`
 * (GIFTWEB-21 AC 1/2).
 *
 * `elapsedRef` mirrors the state so the value can be read synchronously from
 * the recorder's onStop — when the limit is what stopped the recording, onStop
 * runs inside the same tick as the final increment, before React has
 * re-rendered.
 */
export function useRecordingElapsed(
  isRecording: boolean,
  limitSeconds: number,
  onLimitReached: () => void,
) {
  const [elapsed, setElapsed] = useState(0);
  const elapsedRef = useRef(0);
  const onLimit = useRef(onLimitReached);

  useEffect(() => {
    onLimit.current = onLimitReached;
  });

  useEffect(() => {
    if (!isRecording) return;

    let count = elapsedRef.current;
    const id = setInterval(() => {
      count += 1;
      elapsedRef.current = count;
      setElapsed(count);
      if (count >= limitSeconds) {
        clearInterval(id);
        onLimit.current();
      }
    }, 1000);

    return () => clearInterval(id);
  }, [isRecording, limitSeconds]);

  /** Call from the start / delete handlers — never from an effect. */
  const reset = useCallback(() => {
    elapsedRef.current = 0;
    setElapsed(0);
  }, []);

  return { elapsed, elapsedRef, reset };
}
